// src/pages/StudentDashboard.js
import React, { useState, useMemo } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { MOCK_VIDEOS, CATEGORIES } from "../data/mockData";
import VideoCard from "../components/student/VideoCard";
import VideoPlayer from "../components/student/VideoPlayer";

export default function StudentDashboard() {
  return (
    <Routes>
      <Route index element={<Home />} />
      <Route path="library" element={<Library />} />
      <Route path="progress" element={<Progress />} />
    </Routes>
  );
}

function Home() {
  const { currentUser } = useAuth();
  const [activeVideo, setActiveVideo] = useState(null);
  const [watched, setWatched] = useState(() => {
    const saved = localStorage.getItem("sp_watched");
    return saved ? JSON.parse(saved) : [];
  });

  function handleWatch(video) { setActiveVideo(video); }
  function handleClose() { setActiveVideo(null); }
  function markWatched(id) {
    setWatched(prev => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      localStorage.setItem("sp_watched", JSON.stringify(next));
      return next;
    });
  }

  const featured = MOCK_VIDEOS.filter(v => v.featured);
  const recent = MOCK_VIDEOS.slice(0, 4);

  return (
    <div style={{ minHeight: "100vh", background: "var(--sp-surface)", fontFamily: "var(--font-body)" }}>
      {/* Hero */}
      <div style={{
        background: "var(--sp-black)",
        padding: "48px 24px 56px",
        position: "relative", overflow: "hidden"
      }}>
        <div style={{
          position: "absolute", top: 0, right: 0, width: 400, height: 400,
          background: "radial-gradient(circle, rgba(255,214,0,0.06) 0%, transparent 70%)",
          pointerEvents: "none"
        }} />
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ animation: "fadeIn 0.5s ease" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{
                width: 8, height: 8, borderRadius: "50%",
                background: "var(--sp-yellow)", boxShadow: "0 0 12px var(--sp-yellow)"
              }} />
              <span style={{ fontSize: 13, color: "var(--sp-yellow)", fontWeight: 600, letterSpacing: "0.06em" }}>
                STUDENT PORTAL
              </span>
            </div>
            <h1 style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(36px, 6vw, 64px)",
              color: "var(--sp-white)", letterSpacing: "0.03em",
              lineHeight: 1.05, marginBottom: 16
            }}>
              WELCOME BACK,<br />
              <span style={{ color: "var(--sp-yellow)" }}>
                {currentUser?.name?.toUpperCase().split(" ")[0] || "STUDENT"}
              </span>
            </h1>
            <p style={{ fontSize: 16, color: "var(--sp-gray-mid)", maxWidth: 480 }}>
              Keep pushing. {MOCK_VIDEOS.length - watched.length} videos left in your curriculum.
            </p>
          </div>

          {/* Stats row */}
          <div style={{
            display: "flex", gap: 16, marginTop: 32,
            flexWrap: "wrap"
          }}>
            {[
              { label: "Videos Available", value: MOCK_VIDEOS.length },
              { label: "Completed", value: watched.length },
              { label: "Completion Rate", value: Math.round((watched.length / MOCK_VIDEOS.length) * 100) + "%" }
            ].map((stat, i) => (
              <div key={i} className={`animate-fade-in stagger-${i + 1}`} style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: "var(--radius-md)",
                padding: "14px 20px"
              }}>
                <div style={{ fontSize: 26, fontWeight: 700, color: "var(--sp-yellow)", fontFamily: "var(--font-display)", letterSpacing: "0.03em" }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: 12, color: "var(--sp-gray-mid)", marginTop: 2 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 24px" }}>
        {/* Progress bar */}
        <div style={{
          background: "var(--sp-white)", borderRadius: "var(--radius-md)",
          border: "1px solid var(--sp-border)", padding: "16px 20px", marginBottom: 40,
          animation: "fadeIn 0.5s ease 0.1s both"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Overall Progress</span>
            <span style={{ fontSize: 13, color: "var(--sp-gray-mid)" }}>
              {watched.length} / {MOCK_VIDEOS.length} videos
            </span>
          </div>
          <div style={{ height: 8, background: "#f0f0f0", borderRadius: 99, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: `${(watched.length / MOCK_VIDEOS.length) * 100}%`,
              background: "var(--sp-yellow)",
              borderRadius: 99,
              transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)"
            }} />
          </div>
        </div>

        {/* Featured section */}
        <Section title="Featured Lectures">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
            {featured.map((v, i) => (
              <div key={v.id} className={`animate-fade-in stagger-${i + 1}`}>
                <VideoCard video={v} onClick={handleWatch} watched={watched.includes(v.id)} />
              </div>
            ))}
          </div>
        </Section>

        {/* Recent uploads */}
        <Section title="Recently Added">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
            {recent.map((v, i) => (
              <div key={v.id} className={`animate-fade-in stagger-${i + 1}`}>
                <VideoCard video={v} onClick={handleWatch} watched={watched.includes(v.id)} />
              </div>
            ))}
          </div>
        </Section>
      </div>

      {activeVideo && (
        <VideoPlayer
          video={activeVideo}
          onClose={handleClose}
          onWatched={markWatched}
        />
      )}
    </div>
  );
}

function Library() {
  const [activeVideo, setActiveVideo] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [watched, setWatched] = useState(() => {
    const saved = localStorage.getItem("sp_watched");
    return saved ? JSON.parse(saved) : [];
  });

  function markWatched(id) {
    setWatched(prev => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      localStorage.setItem("sp_watched", JSON.stringify(next));
      return next;
    });
  }

  const filtered = useMemo(() => {
    return MOCK_VIDEOS.filter(v => {
      const matchCat = category === "All" || v.category === category;
      const matchSearch = !search || v.title.toLowerCase().includes(search.toLowerCase()) ||
        v.description.toLowerCase().includes(search.toLowerCase()) ||
        v.instructor.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [search, category]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--sp-surface)", padding: "32px 24px", fontFamily: "var(--font-body)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: 40, letterSpacing: "0.04em", marginBottom: 8 }}>
          VIDEO LIBRARY
        </h1>
        <p style={{ color: "var(--sp-gray-mid)", marginBottom: 28 }}>
          Browse all {MOCK_VIDEOS.length} lectures across subjects.
        </p>

        {/* Search */}
        <div style={{ position: "relative", marginBottom: 20 }}>
          <span style={{
            position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
            fontSize: 16, color: "var(--sp-gray-mid)", pointerEvents: "none"
          }}>🔍</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search lectures, subjects, instructors…"
            style={{
              width: "100%", padding: "12px 14px 12px 42px",
              fontSize: 14, background: "var(--sp-white)",
              border: "1px solid var(--sp-border)", borderRadius: "var(--radius-md)",
              outline: "none", fontFamily: "var(--font-body)",
              transition: "border-color 0.2s"
            }}
            onFocus={e => { e.target.style.borderColor = "var(--sp-yellow)"; }}
            onBlur={e => { e.target.style.borderColor = "var(--sp-border)"; }}
          />
        </div>

        {/* Category pills */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 28 }}>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              style={{
                padding: "7px 16px", fontSize: 13, fontWeight: 600,
                background: category === cat ? "var(--sp-yellow)" : "var(--sp-white)",
                color: category === cat ? "var(--sp-black)" : "var(--sp-gray-mid)",
                border: category === cat ? "1px solid var(--sp-yellow)" : "1px solid var(--sp-border)",
                borderRadius: 99, cursor: "pointer",
                transition: "all 0.15s ease", fontFamily: "var(--font-body)"
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: "var(--sp-gray-mid)" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🎬</div>
            <p style={{ fontSize: 16 }}>No videos match your search.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
            {filtered.map((v, i) => (
              <div key={v.id} className={`animate-fade-in stagger-${Math.min(i + 1, 5)}`}>
                <VideoCard video={v} onClick={setActiveVideo} watched={watched.includes(v.id)} />
              </div>
            ))}
          </div>
        )}
      </div>

      {activeVideo && (
        <VideoPlayer
          video={activeVideo}
          onClose={() => setActiveVideo(null)}
          onWatched={markWatched}
        />
      )}
    </div>
  );
}

function Progress() {
  const { currentUser } = useAuth();
  const watched = JSON.parse(localStorage.getItem("sp_watched") || "[]");
  const watchedVideos = MOCK_VIDEOS.filter(v => watched.includes(v.id));
  const byCategory = CATEGORIES.filter(c => c !== "All").map(cat => {
    const total = MOCK_VIDEOS.filter(v => v.category === cat).length;
    const done = watchedVideos.filter(v => v.category === cat).length;
    return { cat, total, done };
  }).filter(c => c.total > 0);

  return (
    <div style={{ minHeight: "100vh", background: "var(--sp-surface)", padding: "32px 24px", fontFamily: "var(--font-body)" }}>
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: 40, letterSpacing: "0.04em", marginBottom: 8 }}>
          MY PROGRESS
        </h1>
        <p style={{ color: "var(--sp-gray-mid)", marginBottom: 32 }}>
          Track your learning journey across all subjects.
        </p>

        {/* Summary card */}
        <div style={{
          background: "var(--sp-black)", borderRadius: "var(--radius-lg)",
          padding: "28px 32px", marginBottom: 28, color: "var(--sp-white)",
          animation: "fadeIn 0.4s ease"
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
            {[
              { label: "Videos Watched", value: watched.length },
              { label: "Total Available", value: MOCK_VIDEOS.length },
              { label: "Completion", value: Math.round((watched.length / MOCK_VIDEOS.length) * 100) + "%" }
            ].map((s, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 44, color: "var(--sp-yellow)", letterSpacing: "0.02em" }}>
                  {s.value}
                </div>
                <div style={{ fontSize: 12, color: "var(--sp-gray-mid)", marginTop: 2 }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* By subject */}
        <div style={{
          background: "var(--sp-white)", borderRadius: "var(--radius-lg)",
          border: "1px solid var(--sp-border)", padding: "24px 28px", marginBottom: 28,
          animation: "fadeIn 0.4s ease 0.1s both"
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Progress by Subject</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {byCategory.map(({ cat, total, done }, i) => (
              <div key={cat}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{cat}</span>
                  <span style={{ fontSize: 12, color: "var(--sp-gray-mid)" }}>{done}/{total}</span>
                </div>
                <div style={{ height: 7, background: "#f0f0f0", borderRadius: 99, overflow: "hidden" }}>
                  <div style={{
                    height: "100%",
                    width: `${(done / total) * 100}%`,
                    background: done === total ? "var(--sp-green)" : "var(--sp-yellow)",
                    borderRadius: 99,
                    transition: `width 0.8s cubic-bezier(0.4, 0, 0.2, 1) ${i * 0.08}s`
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Watched list */}
        {watchedVideos.length > 0 && (
          <div style={{
            background: "var(--sp-white)", borderRadius: "var(--radius-lg)",
            border: "1px solid var(--sp-border)", padding: "24px 28px",
            animation: "fadeIn 0.4s ease 0.2s both"
          }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Completed Videos</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {watchedVideos.map(v => (
                <div key={v.id} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 12px",
                  background: "var(--sp-green-light)",
                  borderRadius: "var(--radius-sm)"
                }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: "50%", background: "var(--sp-green)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontSize: 11, fontWeight: 700, flexShrink: 0
                  }}>✓</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.title}</div>
                    <div style={{ fontSize: 11, color: "var(--sp-gray-mid)" }}>{v.category} · {v.instructor}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 48 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ width: 4, height: 22, background: "var(--sp-yellow)", borderRadius: 2 }} />
        <h2 style={{ fontSize: 20, fontWeight: 700, fontFamily: "var(--font-display)", letterSpacing: "0.04em" }}>
          {title.toUpperCase()}
        </h2>
      </div>
      {children}
    </div>
  );
}
